/** @jsx isolateComponent */
import { useMemo, useState } from 'react';
import { css } from '@emotion/core';

import { isolateComponent } from '@zapier/style-encapsulation';
import { Icon, Colors, Typography } from '@zapier/design-system';
import { ServiceIconShell } from '@zapier/design-system-patterns';

import { ServiceIcon } from '../../shared/ServiceIcon';
import ExternalLink from '../../shared/ExternalLink';

import { Incident as IncidentType } from './types';
import { groupIncidents } from './utils';

type IncidentsProps = {
  /** Displayed in the yellow header. */
  title: string;
  /** A list of incidents. */
  incidents: IncidentType[];
};

type IncidentProps = IncidentType & {
  showIcon: boolean;
};

const headingCss = css`
  background-color: ${Colors.warning10};
  height: 58px;
  display: grid;
  grid-column-gap: 19px;
  grid-template-columns: min-content 1fr min-content;
  align-items: center;
  padding-left: 17px;
  padding-right: 30px;
  ${Typography.smallPrint1Semibold}
  color: ${Colors.neutral800};
`;

const toggleCss = css`
  white-space: nowrap;
  &:hover {
    color: ${Colors.blue};
    text-decoration: underline;
  }
`;

const listCss = css`
  padding: 21px 55px 18px 13px;
  display: grid;
  grid-row-gap: 20px;
`;

const rowCss = css`
  padding-left: 13px;
  display: grid;
  grid-column-gap: 16px;
  grid-template-columns: min-content 1fr;
`;

const maybeIconCss = css`
  padding-top: 4px;
  width: 30px;
`;

const incidentInfoCss = css`
  display: grid;
  grid-row-gap: 5px;
`;

const incidentTitleCss = css`
  ${Typography.smallPrint1Semibold}
  color: ${Colors.neutral800};
`;

const incidentBodyCss = css`
  ${Typography.smallPrint1}
  color: ${Colors.neutral600};
`;

const linkCss = css`
  ${Typography.smallPrint1Semibold}
  text-decoration: underline;
  color: ${Colors.blue};
  &:hover {
    color: ${Colors.night};
  }
`;

const Incident = ({
  showIcon,
  iconApp,
  status,
  name,
  body,
  shortlink,
}: IncidentProps) => {
  return (
    <li css={rowCss}>
      <div css={maybeIconCss}>
        {showIcon &&
          (iconApp ? (
            <ServiceIcon serviceName={iconApp} size={30} />
          ) : (
            <ServiceIconShell size={30}>
              <Icon color="error300" name="miscBoltOutlined" size={24} />
            </ServiceIconShell>
          ))}
      </div>
      <div css={incidentInfoCss}>
        <p css={incidentTitleCss}>
          {status[0].toUpperCase()}
          {status.slice(1)}: {name}
        </p>
        <p css={incidentBodyCss}>{body}</p>
        {shortlink && (
          <ExternalLink css={linkCss} href={shortlink}>
            View all updates
          </ExternalLink>
        )}
      </div>
    </li>
  );
};

/** Renders an expandable block with incidents. */
const Incidents = ({ title, incidents }: IncidentsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const groups = useMemo(() => groupIncidents(incidents), [incidents]);

  const toggleIsOpen = () => setIsOpen(!isOpen);
  return (
    <div>
      <div css={headingCss}>
        <Icon color="neutral700" name="alertInfoFill" size={24} />
        <span>{title}</span>
        <button css={toggleCss} onClick={toggleIsOpen}>
          {isOpen ? 'Hide' : 'Learn more'}
        </button>
      </div>
      {isOpen && (
        <ol css={listCss}>
          {groups.map((groupOfIncidents, g) =>
            groupOfIncidents.map((incident, i) => (
              <Incident key={`${g}-${i}`} showIcon={i === 0} {...incident} />
            ))
          )}
        </ol>
      )}
    </div>
  );
};

export default Incidents;
